// components/ShareBar.tsx
import React, { useMemo, useState } from "react";

type ShareLinks = {
  x?: string;
  linkedin?: string; // LI ignores text; uses OG tags on the URL
  mastodon?: string;
  bluesky?: string;
};

interface ShareBarProps {
  shareLinks?: ShareLinks;
  absoluteUrl?: string;   // Should be the public canonical URL of the post
  title?: string;
  message?: string;       // Custom message to copy and use in X/Masto/Bluesky
  popup?: boolean;
  /** Copy message to clipboard before opening LinkedIn */
  copyMessageForLinkedIn?: boolean;
  /** Show a helper link to LinkedIn Post Inspector (cache refresher) */
  showLinkedInInspector?: boolean;
}

const ensureAbsolute = (url?: string) => {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  try {
    return new URL(url, window.location.origin).toString();
  } catch {
    return url;
  }
};

const buildShareLinks = ({
  absoluteUrl,
  title,
  message,
}: {
  absoluteUrl: string;
  title?: string;
  message?: string;
}): ShareLinks => {
  const url = encodeURIComponent(absoluteUrl);
  const text = encodeURIComponent(
    `${message || "Discover more about my featured blog on the LIBSENSE website."}${
      title ? ` — ${title}` : ""
    }`
  );

  return {
    x: `https://x.com/intent/tweet?text=${text}&url=${url}`,
    // LinkedIn: preview comes from OG tags on the URL
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    mastodon: `https://mastodon.social/share?text=${text}%20${url}`,
    bluesky: `https://bsky.app/intent/compose?text=${text}%20${url}`,
  };
};

const openPopup = (href: string) => {
  const w = 640, h = 640;
  const y = Math.max(0, (window.outerHeight - h) / 2);
  const x = Math.max(0, (window.outerWidth - w) / 2);
  window.open(href, "_blank", `width=${w},height=${h},left=${x},top=${y},noopener`);
};

const ShareBar: React.FC<ShareBarProps> = ({
  shareLinks,
  absoluteUrl,
  title,
  message,
  popup = true,
  copyMessageForLinkedIn = true,
  showLinkedInInspector = true,
}) => {
  const [copied, setCopied] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  const absolute = ensureAbsolute(absoluteUrl);

  const links = useMemo<ShareLinks | undefined>(() => {
    if (shareLinks && Object.keys(shareLinks).length > 0) return shareLinks;
    if (!absolute) return undefined;
    return buildShareLinks({ absoluteUrl: absolute, title, message });
  }, [shareLinks, absolute, title, message]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.error("Clipboard copy failed", e);
    }
  };

  const onClick = (href?: string) => {
    if (!href) return;
    popup ? openPopup(href) : window.open(href, "_blank", "noopener");
  };

  if (!links && !absolute) return null;

  const inspectorHref = absolute
    ? `https://www.linkedin.com/post-inspector/inspect/?url=${encodeURIComponent(absolute)}`
    : undefined;

  return (
    <div className="flex gap-2 mt-3 items-center flex-wrap">
      {/* X */}
      {links?.x && (
        <button
          onClick={() => onClick(links.x)}
          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xxs text-muted-foreground hover:bg-card hover:text-foreground transition"
          title="Share on X"
          aria-label="Share on X"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M18.9 2H22l-7.7 8.8L22.7 22H16l-5.1-6.6L5 22H2l8.3-9.5L2 2h6l4.7 6.1L18.9 2z"/></svg>
          X
        </button>
      )}

      {/* LinkedIn (copy message first, user pastes in “Add description”) */}
      {links?.linkedin && (
        <button
          onClick={async () => {
            if (copyMessageForLinkedIn && message) {
              await handleCopy(message);
              setHint("Message copied. Paste it in LinkedIn’s ‘Add description’ field.");
              setTimeout(() => setHint(null), 2500);
            }
            onClick(links.linkedin);
          }}
          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xxs text-muted-foreground hover:bg-card hover:text-foreground transition"
          title="Share on LinkedIn"
          aria-label="Share on LinkedIn"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8h4V24h-4zM8 8h3.8v2.2h.06c.53-1 1.83-2.2 3.77-2.2C20.4 8 24 10.6 24 16.1V24h-4v-6.9c0-1.6-.03-3.7-2.26-3.7-2.26 0-2.61 1.77-2.61 3.6V24H11.1V8z"/></svg>
          LinkedIn
        </button>
      )}

      {/* Mastodon */}
      {links?.mastodon && (
        <button
          onClick={() => onClick(links.mastodon)}
          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xxs text-muted-foreground hover:bg-card hover:text-foreground transition"
          title="Share on Mastodon"
          aria-label="Share on Mastodon"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M21.57 7.74c-.3-2.27-2.28-4.06-4.7-4.36C15.9 3.14 14.05 3 12.1 3h-.2c-1.95 0-3.8.14-4.78.38-2.42.3-4.4 2.09-4.7 4.36-.31 2.37-.42 5.77.2 8.01.45 1.67 2.2 3.04 3.93 3.62 1.85.62 3.86.72 5.84.72s4-.1 5.85-.72c1.73-.58 3.48-1.95 3.92-3.62.63-2.24.52-5.64.21-8.01zM16 16.5h-2V11.7c0-1.1-1.4-1.14-1.4.06v4.74H10V11.7c0-1.1-1.4-1.14-1.4.06v4.74H6.6V9h2v1.03c.58-.88 3.12-.93 3.5.54.66-1.02 3.4-1.17 3.9.78V16.5z"/></svg>
          Mastodon
        </button>
      )}

      {/* Bluesky */}
      {links?.bluesky && (
        <button
          onClick={() => onClick(links.bluesky)}
          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xxs text-muted-foreground hover:bg-card hover:text-foreground transition"
          title="Share on Bluesky"
          aria-label="Share on Bluesky"
        >
          <svg width="14" height="14" viewBox="0 0 256 256" aria-hidden="true"><path fill="currentColor" d="M128 138c-20-33-53-66-77-86c-9-8-28-21-39-21c-7 0-12 6-12 14c0 7 4 15 9 22c13 19 33 34 54 44c-20 9-39 23-54 41c-7 8-12 17-12 25c0 9 6 15 14 15c15 0 36-14 46-22c25-19 50-46 71-72c21 26 46 53 71 72c10 8 31 22 46 22c8 0 14-6 14-15c0-8-5-17-12-25c-15-18-34-32-54-41c21-10 41-25 54-44c5-7 9-15 9-22c0-8-5-14-12-14c-11 0-30 13-39 21c-24 20-57 53-77 86Z"/></svg>
          Bluesky
        </button>
      )}

      {/* Copy link */}
      {absolute && (
        <button
          onClick={() => handleCopy(absolute)}
          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xxs text-muted-foreground hover:bg-card hover:text-foreground transition"
          title="Copy Link"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M3.9 12a4 4 0 0 1 4-4h4v2h-4a2 2 0 0 0 0 4h4v2h-4a4 4 0 0 1-4-4Zm6-1h4a4 4 0 1 1 0 8h-4v-2h4a2 2 0 1 0 0-4h-4v-2Z"/></svg>
          {copied ? <span className="text-green-600">Copied!</span> : <span>Copy link</span>}
        </button>
      )}

      {/* Small hint for LI flow */}
      {hint && <span className="text-xxs text-muted-foreground ml-1">{hint}</span>}

      {/* Optional: LinkedIn Post Inspector link to refresh cached preview */}
      {showLinkedInInspector && inspectorHref && (
        <a
          href={inspectorHref}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-2 text-xxs underline text-muted-foreground hover:text-foreground"
          title="Refresh LinkedIn preview cache"
        >
          Refresh LinkedIn preview
        </a>
      )}
    </div>
  );
};

export default ShareBar;
